import { type JurisdictionRow, currencyLabel } from '@mollusk/shared';

/**
 * Pure column model + filter/sort helpers for the Permit Detail table. Kept
 * free of React/MUI so the sorting, filtering, and column-grouping rules can be
 * unit-tested directly.
 */

export type ColumnGroup = 'core' | 'geography' | 'activity' | 'quality' | 'status' | 'freshness';

export interface ColumnDef {
  id: keyof JurisdictionRow;
  label: string;
  group: ColumnGroup;
  /** Right-align + numeric sort. */
  numeric: boolean;
  /** Render hint for the cell formatter. */
  kind?: 'pct' | 'int' | 'num' | 'bool' | 'date' | 'text';
  /**
   * Derived cell value used for both display and sorting. Defaults to
   * `row[id]`. The Trades/Tags summary columns use this to surface the number
   * of distinct values inside the nested `trade_counts` / `tag_counts` maps
   * (which can't sort as raw objects).
   */
  value?: (row: JurisdictionRow) => string | number | boolean | null;
}

export const COLUMN_GROUP_LABELS: Record<ColumnGroup, string> = {
  core: 'Core',
  geography: 'Geography',
  activity: 'Trades & tags',
  quality: 'Fill rates',
  status: 'Status & blockers',
  freshness: 'Freshness',
};

/** Distinct-value count of a nested counts map, or null when empty (renders '—'). */
function distinctCount(counts: Readonly<Record<string, number>>): number | null {
  const n = Object.keys(counts).length;
  return n > 0 ? n : null;
}

/** `[value, count]` pairs, busiest first then alphabetical — for the detail panel + CSV. */
export function sortedCountEntries(counts: Readonly<Record<string, number>>): [string, number][] {
  return Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** Column order = display order. Core first, then optional groups. */
export const COLUMNS: readonly ColumnDef[] = [
  { id: 'jurisdiction_name', label: 'Jurisdiction', group: 'core', numeric: false, kind: 'text' },
  { id: 'kind', label: 'Type', group: 'core', numeric: false, kind: 'text' },
  { id: 'state', label: 'State', group: 'core', numeric: false, kind: 'text' },
  { id: 'bps_volume', label: 'BPS volume', group: 'core', numeric: true, kind: 'int' },
  { id: 'permit_count', label: 'Our permits', group: 'core', numeric: true, kind: 'int' },
  { id: 'grade', label: 'Grade', group: 'core', numeric: false, kind: 'text' },
  { id: 'platform', label: 'Scraper', group: 'core', numeric: false, kind: 'text' },
  { id: 'coverage_start', label: 'Earliest permit', group: 'core', numeric: false, kind: 'date' },
  { id: 'coverage_end', label: 'Coverage end', group: 'core', numeric: false, kind: 'date' },
  {
    id: 'permits_last_12mo',
    label: 'Permits (last 12mo)',
    group: 'core',
    numeric: true,
    kind: 'int',
  },
  { id: 'permits_per_10k', label: 'Permits / 10K pop', group: 'core', numeric: true, kind: 'num' },
  {
    // Feed currency (molluskio#4153): flags thin-tail/stale feeds so a jurisdiction
    // that stopped updating isn't read as current. Absent field → 'Undated'.
    id: 'currency_status',
    label: 'Currency',
    group: 'core',
    numeric: false,
    kind: 'text',
    value: (r) => currencyLabel(r.currency_status ?? 'dateless'),
  },

  {
    id: 'trade_counts',
    label: 'Trades',
    group: 'activity',
    numeric: true,
    kind: 'int',
    value: (r) => distinctCount(r.trade_counts),
  },
  {
    id: 'tag_counts',
    label: 'Tags',
    group: 'activity',
    numeric: true,
    kind: 'int',
    value: (r) => distinctCount(r.tag_counts),
  },

  { id: 'county_name', label: 'County', group: 'geography', numeric: false, kind: 'text' },
  { id: 'cbsa_name', label: 'Metro (CBSA)', group: 'geography', numeric: false, kind: 'text' },
  { id: 'population', label: 'Population', group: 'geography', numeric: true, kind: 'int' },

  { id: 'pct_dated', label: '% dated', group: 'quality', numeric: true, kind: 'pct' },
  { id: 'pct_geocodeable', label: '% with location', group: 'quality', numeric: true, kind: 'pct' },
  { id: 'pct_resolvable', label: '% resolvable', group: 'quality', numeric: true, kind: 'pct' },
  { id: 'pct_error', label: '% error', group: 'quality', numeric: true, kind: 'pct' },

  { id: 'status', label: 'Status', group: 'status', numeric: false, kind: 'text' },
  { id: 'looks_complete', label: 'Looks complete', group: 'status', numeric: false, kind: 'bool' },
  { id: 'blocker_reason', label: 'Blocker', group: 'status', numeric: false, kind: 'text' },
  { id: 'actionable_next_step', label: 'Next step', group: 'status', numeric: false, kind: 'text' },
  { id: 'notes', label: 'Notes', group: 'status', numeric: false, kind: 'text' },

  {
    id: 'last_fetched_at',
    label: 'Last fetched',
    group: 'freshness',
    numeric: false,
    kind: 'date',
  },
  { id: 'last_mapped_at', label: 'Last mapped', group: 'freshness', numeric: false, kind: 'date' },
  {
    id: 'last_evaluated_at',
    label: 'Last evaluated',
    group: 'freshness',
    numeric: false,
    kind: 'date',
  },
];

/** Columns shown by default (everything; the widget lets the user trim). */
export function defaultVisibleColumns(): Set<keyof JurisdictionRow> {
  return new Set(COLUMNS.map((c) => c.id));
}

export interface JurisdictionFilters {
  /** Case-insensitive substring match on jurisdiction name. */
  search: string;
  /** Exact state code, or null for any. */
  state: string | null;
  /** Exact letter grade, or null for any. */
  grade: string | null;
  /** Census place vs county permit authority, or null for both. */
  kind: 'place' | 'county' | null;
  /** Restrict to jurisdictions we actually hold permits for. */
  coveredOnly: boolean;
}

export const EMPTY_FILTERS: JurisdictionFilters = {
  search: '',
  state: null,
  grade: null,
  kind: null,
  coveredOnly: false,
};

export function filterJurisdictions(
  rows: readonly JurisdictionRow[],
  filters: JurisdictionFilters,
): JurisdictionRow[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (needle && !r.jurisdiction_name.toLowerCase().includes(needle)) return false;
    if (filters.state && r.state !== filters.state) return false;
    if (filters.grade && r.grade !== filters.grade) return false;
    if (filters.kind && r.kind !== filters.kind) return false;
    if (filters.coveredOnly && !r.covered) return false;
    return true;
  });
}

export type SortDir = 'asc' | 'desc';

function compare(a: unknown, b: unknown): number {
  // Nulls always sink to the bottom regardless of direction.
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b));
}

/** Stable sort by `key`; nulls always last, then `dir` flips the rest. */
export function sortJurisdictions(
  rows: readonly JurisdictionRow[],
  key: keyof JurisdictionRow,
  dir: SortDir,
): JurisdictionRow[] {
  const withNull = (v: unknown) => v === null || v === undefined;
  return [...rows].sort((ra, rb) => {
    const a = ra[key];
    const b = rb[key];
    if (withNull(a) || withNull(b)) return compare(a, b); // keep nulls last both ways
    const base = compare(a, b);
    return dir === 'asc' ? base : -base;
  });
}

/** The value a column contributes to a row — its `value` accessor, else `row[id]`. */
export function columnValue(row: JurisdictionRow, col: ColumnDef): unknown {
  return col.value ? col.value(row) : row[col.id];
}

/**
 * Column-aware sort: orders by each column's derived value (so the Trades/Tags
 * summary columns sort by distinct count, not by their raw map object). Nulls
 * always sink to the bottom, then `dir` flips the rest — same rule as
 * `sortJurisdictions`.
 */
export function sortByColumn(
  rows: readonly JurisdictionRow[],
  col: ColumnDef,
  dir: SortDir,
): JurisdictionRow[] {
  const withNull = (v: unknown) => v === null || v === undefined;
  return [...rows].sort((ra, rb) => {
    const a = columnValue(ra, col);
    const b = columnValue(rb, col);
    if (withNull(a) || withNull(b)) return compare(a, b);
    const base = compare(a, b);
    return dir === 'asc' ? base : -base;
  });
}

/** Distinct, sorted non-null values of a column (for filter dropdowns). */
export function distinctValues(
  rows: readonly JurisdictionRow[],
  key: keyof JurisdictionRow,
): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = r[key];
    if (typeof v === 'string' && v) set.add(v);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
